/**
 * AnalyticsUserSync Component
 * Syncs user authentication state with PostHog for user identification
 * Place this component within both AuthProvider and PostHogProvider
 */

import { useEffect, useRef } from 'react';
import { usePostHogSafe } from '../../context/PostHogContext';
import { useAuth } from '../../context/AuthContext';
import { getConfig } from '@/config/env';
import type { AnalyticsUserProperties, VendorGroupProperties } from '../../types/analytics';

/**
 * Extract low-cardinality user properties from currentUser
 * Only includes non-PII data suitable for analytics
 */
function extractUserProperties(currentUser: any): AnalyticsUserProperties {
  if (!currentUser) {
    return {};
  }

  const config = getConfig();

  return {
    // Role/permission level (low cardinality)
    operator_role: currentUser.role || currentUser.operatorRole || 'operator',
    // Vendor/tenant info (low cardinality)
    vendor_id: currentUser.vendorId?.toString() || currentUser.vendor?.id?.toString(),
    vendor_name: currentUser.vendorName || currentUser.vendor?.name,
    // Permission flags (boolean, very low cardinality)
    has_super_permissions: Boolean(
      currentUser.isSuperAdmin ||
      currentUser.superPermissions ||
      currentUser.role === 'super_admin'
    ),
    // App environment
    app_env: config.APP_ENV
  };
}

/**
 * Extract vendor group properties from currentUser
 * Only includes low-cardinality vendor data
 */
function extractVendorProperties(currentUser: any): VendorGroupProperties {
  if (!currentUser?.vendor) {
    return {};
  }

  return {
    name: currentUser.vendor.name,
    // Add other vendor properties as needed (tier, region, etc.)
  };
}

/**
 * Get vendor ID from currentUser
 */
function getVendorId(currentUser: any): string | null {
  if (!currentUser) {
    return null;
  }

  const vendorId = currentUser.vendorId || currentUser.vendor?.id;
  return vendorId ? vendorId.toString() : null;
}

/**
 * Generate a stable operator ID for PostHog identification
 * Uses the operator's internal ID, NOT PII like email
 */
function getOperatorId(currentUser: any): string | null {
  if (!currentUser) {
    return null;
  }

  // Prefer internal operator ID
  if (currentUser.id) {
    return `operator_${currentUser.id}`;
  }

  // Fallback to other unique identifiers
  if (currentUser.operatorId) {
    return `operator_${currentUser.operatorId}`;
  }

  return null;
}

/**
 * AnalyticsUserSync Component
 * Automatically identifies users with PostHog when auth state changes
 * Also sets the vendor group for Group Analytics segmentation
 */
export function AnalyticsUserSync() {
  const posthog = usePostHogSafe();
  const { initialState } = useAuth();
  const previousUserIdRef = useRef<string | null>(null);
  const previousVendorIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip if PostHog is not enabled
    if (!posthog?.isEnabled) {
      return;
    }

    const currentUser = initialState?.currentUser;
    const operatorId = getOperatorId(currentUser);
    const vendorId = getVendorId(currentUser);

    // User logged out or no user
    if (!operatorId) {
      // Only reset if we previously had a user
      if (previousUserIdRef.current) {
        posthog.reset();
        previousUserIdRef.current = null;
        previousVendorIdRef.current = null;
      }
      return;
    }

    // User changed or first identification
    if (previousUserIdRef.current !== operatorId) {
      const userProperties = extractUserProperties(currentUser);
      posthog.identify(operatorId, userProperties);
      previousUserIdRef.current = operatorId;
    }

    // Set vendor group for Group Analytics
    // This enables vendor-level segmentation and analytics
    if (vendorId && previousVendorIdRef.current !== vendorId) {
      const vendorProperties = extractVendorProperties(currentUser);
      posthog.setGroup('vendor', vendorId, vendorProperties);
      previousVendorIdRef.current = vendorId;
    }
  }, [posthog, initialState?.currentUser]);

  // This component doesn't render anything
  return null;
}

export default AnalyticsUserSync;
